import React, { useCallback, useEffect, useState } from 'react';
import { CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { debounce } from 'lodash';
import { AutocompleteOption } from 'types';

type Props<T> = {
  value: string | AutocompleteOption | undefined;
  onChangeApiCall: (query: string) => Promise<T[]>;
  onBlur?: (value: string) => void;
  getOptionLabel?: (option: AutocompleteOption | string) => string;
  onSelect: (
    event: React.ChangeEvent<{}>,
    value: AutocompleteOption | null | string
  ) => void;
  mapOption: (item: T) => AutocompleteOption | string;
  getOptionIsSelected?: (
    option: AutocompleteOption | string,
    value: AutocompleteOption | string
  ) => boolean;
  hint: string;
  label: string;
  id: string;
  error: string;
  freeSolo?: boolean;
  disabled?: boolean;
};

function RemoteAutocomplete<T>({
  value,
  onChangeApiCall,
  onSelect,
  mapOption,
  hint,
  label,
  error,
  id,
  freeSolo,
  getOptionLabel = (option: AutocompleteOption | string) =>
    (option as AutocompleteOption)?.label ?? '',
  getOptionIsSelected = (
    option: AutocompleteOption | string,
    value: AutocompleteOption | string
  ) => (option as AutocompleteOption)?.id === (value as AutocompleteOption)?.id,
  onBlur,
  disabled,
}: Props<T>) {
  const [options, setOptions] = useState<string[] | AutocompleteOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => setOptions([]), [id]);

  const handleSubmit = useCallback(
    async (prompt: string) => {
      setLoading(true);
      const items = await onChangeApiCall(prompt);
      setOptions(items.map((item) => mapOption(item)) as AutocompleteOption[]);
      setLoading(false);
    },
    [onChangeApiCall, mapOption]
  );

  const debouncedSubmit = debounce(handleSubmit, 400);

  return (
    <Autocomplete
      id={id}
      autoComplete
      disabled={disabled}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionIsSelected}
      value={value ?? ((null as unknown) as undefined)}
      noOptionsText="Žádné možnosti..."
      loadingText="Načítám"
      disableClearable
      loading={loading}
      onChange={(event, value) => onSelect(event, value)}
      autoHighlight={false}
      freeSolo={freeSolo}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={hint}
          variant="outlined"
          error={!!error}
          helperText={error}
          onBlur={(e) => {
            // TODO:
            if (freeSolo) {
              onBlur?.(e.target.value);
            } else {
              onBlur?.(value as string);
            }
          }}
          onChange={(e) => debouncedSubmit(e.target.value)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}

export default RemoteAutocomplete;
